// AmenitiesRow.js
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';

const AmenitiesContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1.2),
  alignItems: 'flex-start',
}));

// Modified to accept featured prop
const AmenityItem = styled(Box)(({ theme, featured }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: '20px',
  border: featured 
    ? `1px solid ${theme.palette.primary.main}` 
    : `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: featured 
    ? theme.palette.primary.light + '15' // Very light primary color with transparency
    : 'transparent',
  boxShadow: featured ? `0 0 8px ${theme.palette.primary.main}30` : 'none',
  '&:hover': {
    backgroundColor: featured 
      ? theme.palette.primary.light + '25'
      : theme.palette.grey[50]
  },
  transition: 'all 0.2s ease-in-out'
}));

// Add a star element for featured items
const FeatureBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -8,
  right: -8,
  color: theme.palette.primary.main,
  fontSize: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const AmenitiesRow = ({ amenities, getIconComponent, theme }) => (
  <AmenitiesContainer>
    {amenities.map((amenity) => {
      const IconComponent = getIconComponent(amenity.icon);
      const isFeatured = amenity.is_featured;
      
      return (
        <Tooltip 
          key={amenity.slug || `amenity-${amenity.name}`} 
          title={
            <>
              {isFeatured && <Typography variant="caption" fontWeight="bold">Featured Amenity</Typography>}
              <Typography variant="body2">{amenity.description}</Typography>
            </>
          }
        >
          {/* Wrap AmenityItem in a div to properly handle the key and positioning */}
          <Box sx={{ position: 'relative' }}>
            <AmenityItem featured={isFeatured}>
              <IconComponent 
                sx={{ 
                  fontSize: 18, 
                  color: isFeatured ? theme.palette.primary.main : theme.icons_lite.main 
                }} 
              />
              <Typography 
                sx={{
                  fontSize: 10,
                  fontWeight: isFeatured ? 'bold' : 'normal',
                  color: isFeatured ? theme.palette.primary.main : 'inherit'
                }}
              >
                {amenity.name}
              </Typography>
            </AmenityItem>
            {isFeatured && (
              <FeatureBadge>
                <StarIcon fontSize="inherit" />
              </FeatureBadge>
            )}
          </Box>
        </Tooltip>
      );
    })}
  </AmenitiesContainer>
);

export default AmenitiesRow;